<script setup lang="ts">
import type { ButtonProps } from '#ui/types';
import type { EmbedEvent, PrefillAndIframeAttrsConfig } from '@calcom/embed-core';

import { getApiGetBookingsQueryKey, useApiGetBookings } from '~/apiClient';
import type { CalcomUiConfig } from '~/components/Calcom/Calcom.vue';
// eslint-disable-next-line regex/invalid
import { UserGatedAction } from '~/components/Login/LoginGatedActionInfo.vue';
import { isAuction, type SaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: SaleItemEntity;
    size?: ButtonProps['size'];
}
const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt, td } = useTypedI18n();
const { title: vehicleTitle } = useSaleTitle(() => sale);
const { data: session, isLoggedIn, getSession } = useAuthUtils();
const { calcomEventUrl } = useRuntimeConfig().public;
const { notifySuccess, notifyError } = useNotification();
const { openLoginModal } = useModals();
const apiGetBookingsParams = computed(() => ({ [isAuction(sale) ? 'auctionId' : 'directSaleId']: sale.id }));
const isApiGetBookingEnabled = computed(() => isLoggedIn.value && sale.includesDepotService);
const {
    data: bookings,
    suspense,
    refetch,
} = useApiGetBookings(apiGetBookingsParams, {
    query: { enabled: isApiGetBookingEnabled, queryKey: getApiGetBookingsQueryKey(apiGetBookingsParams) },
});

isApiGetBookingEnabled.value && (await suspense());

// DATA
const open = ref<boolean>(false);

const booking = computed(() => bookings.value?.bookings[0]);
const bookingUid = computed(() => (booking.value ? new URL(booking.value.attendeeRescheduleUrl).pathname.split('/').pop() : undefined));
const bookingDate = computed(() =>
    import.meta.client && booking.value ? `${td(booking.value.start, 'ddmmyyyy')} - ${td(booking.value.start, 'hhmm')}` : ''
);
const calcomConfig = computed<PrefillAndIframeAttrsConfig>(() => ({
    ...(isLoggedIn.value
        ? {
              name: `${session.value?.user?.firstname} ${session.value?.user?.lastname}`,
              email: session.value?.user?.email ?? '',
              attendeePhoneNumber: session.value?.user?.phone ?? '',
              'metadata[userId]': session.value?.user?.id ?? '',
              'metadata[companyId]': session.value?.user.companyId ?? '',
          }
        : {}),
    [`metadata[${isAuction(sale) ? 'auctionId' : 'directSaleId'}]`]: sale.id,
    theme: 'light',
    layout: 'month_view',
    useSlotsViewOnSmallScreen: 'true',
    'cal.embed.pageType': 'user.event.booking.form',
}));
const calcomUiConfig: CalcomUiConfig = {
    hideEventTypeDetails: true,
    theme: 'light',
    cssVarsPerTheme: { light: { 'cal-brand': '#2062f2' } },
    layout: 'month_view',
};

// HANDLERS
const onLogin = async (): Promise<void> => {
    if (!(await openLoginModal({ action: UserGatedAction.SDP_DEPOT_VISIT_BOOK }))) return;

    await getSession();

    if (booking.value) return;

    open.value = true;
};

const onBookingSuccessfulV2 = (e: EmbedEvent<'bookingSuccessfulV2'>): void => {
    notifySuccess({ title: tt('sdp.depot.modal.book.success', { vehicle: vehicleTitle.value, date: td(e.detail.data.startTime ?? '') }) });
    open.value = false;
    refetch();
};

const onRescheduleBookingSuccessfulV2 = (e: EmbedEvent<'rescheduleBookingSuccessfulV2'>): void => {
    notifySuccess({ title: tt('sdp.depot.modal.reschedule.success', { vehicle: vehicleTitle.value, date: td(e.detail.data.startTime ?? '') }) });
    open.value = false;
    refetch();
};

const onLinkFailed = (_e: EmbedEvent<'linkFailed'>): void => {
    notifyError({ title: tt('sdp.depot.modal.error') });
    open.value = false;
};
</script>

<template>
    <UButton v-if="!isLoggedIn" icon="lucide:calendar-heart" :label="tt('sdp.depot.modal.book.cta')" :size="size" @click="onLogin" />
    <template v-else-if="booking">
        <div class="flex flex-row items-center gap-x-2">
            <p class="text-toned text-sm">{{ tt('sdp.depot.resume', { date: bookingDate }) }}</p>
            <UModal
                v-if="!!bookingUid"
                v-model:open="open"
                fullscreen
                :title="tt('sdp.depot.modal.reschedule.title', { vehicle: vehicleTitle, date: bookingDate })"
                :description="tt('sdp.depot.modal.reschedule.description', { vehicle: vehicleTitle })"
            >
                <UButton color="primary" icon="lucide:calendar-sync" :label="tt('sdp.depot.modal.reschedule.cta')" :size="size" />

                <template #body>
                    <Calcom
                        class="h-full w-full"
                        :link="calcomEventUrl"
                        :config="{
                            ...calcomConfig,
                            rescheduleUid: bookingUid,
                        }"
                        :ui-config="calcomUiConfig"
                        @reschedule-booking-successful-v2="onRescheduleBookingSuccessfulV2"
                        @link-failed="onLinkFailed"
                    />
                </template>
            </UModal>
            <UButton color="error" :label="tt('sdp.depot.modal.cancel')" :size="size" :to="booking.attendeeCancelUrl" target="_blank" />
        </div>
    </template>
    <UModal
        v-else
        v-model:open="open"
        fullscreen
        :title="tt('sdp.depot.modal.book.title', { vehicle: vehicleTitle })"
        :description="tt('sdp.depot.modal.book.description', { vehicle: vehicleTitle })"
    >
        <UButton icon="lucide:calendar-heart" :label="tt('sdp.depot.modal.book.cta')" :size="size" />

        <template #body>
            <Calcom
                class="h-full w-full"
                :link="calcomEventUrl"
                :config="calcomConfig"
                :ui-config="calcomUiConfig"
                @booking-successful-v2="onBookingSuccessfulV2"
                @link-failed="onLinkFailed"
            />
        </template>
    </UModal>
</template>
